import { api } from '@/lib/axios';
import type {
  Client,
  ClientDetail,
  ListClientsParams,
  CreateClientPayload,
  UpdateClientPayload,
  CreateAddressPayload,
  UpdateAddressPayload,
  CreateContactPayload,
  DuplicateCandidate,
  Address,
  ClientOptions,
  ContactPerson,
  Ortsvorschlag,
  WebsiteSuggestion,
} from '../types/client.types';

interface PaginatedClients {
  data: Client[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const clientsApi = {
  list: (params?: ListClientsParams) =>
    api.get<PaginatedClients>('/clients', { params }),

  get: (id: string) =>
    api.get<ClientDetail>(`/clients/${id}`),

  /**
   * **Gibt es den schon?** — fragt der Anlage-Dialog, bevor jemand speichert.
   *
   * Dieselbe Prüfung läuft beim `POST` noch einmal; das hier ist der frühe
   * Hinweis, nicht die Sperre.
   */
  findDuplicates: (params: { name?: string; vatId?: string; email?: string; website?: string; excludeId?: string }) =>
    api.get<DuplicateCandidate[]>('/clients/duplicates', { params }),

  /** Die eigenen Adressarten des Mandanten (AP-31 Nachtrag). */
  options: () =>
    api.get<ClientOptions>('/clients/options'),

  /**
   * **PLZ zum Ort, Ort zur PLZ** — ein Vorschlag, keine Prüfung. Scheitert die
   * Abfrage, bleibt das Feld, wie es ist.
   */
  places: (params: { country: string; zip?: string; city?: string }) =>
    api.get<{ data: Ortsvorschlag[] }>('/clients/places', { params }),

  /** Wird „Von der Webseite lesen" angeboten? — nur mit eingeschalteter KI des Betriebs. */
  websiteReadingState: () =>
    api.get<{ data: { available: boolean } }>('/clients/website-reading'),

  /** Name, Anschrift, UID und Kontakt von einer Webseite — ein Vorschlag, angelegt wird nichts. */
  readWebsite: (url: string) =>
    api.post<{ data: WebsiteSuggestion }>('/clients/website-reading', { url }),

  create: (payload: CreateClientPayload) =>
    api.post<Client>('/clients', payload),

  update: (id: string, payload: UpdateClientPayload) =>
    api.patch<Client>(`/clients/${id}`, payload),

  /** Deaktivieren — der Partner bleibt, verschwindet nur aus Auswahlfeldern. */
  delete: (id: string) =>
    api.delete<{ message: string }>(`/clients/${id}`),

  /**
   * **Endgültig löschen** (AP-31 4.8) — braucht `crm.client.delete` und
   * scheitert mit `409`, solange Aufträge am Partner hängen.
   */
  purge: (id: string) =>
    api.delete<void>(`/clients/${id}/permanent`),

  // Addresses
  addAddress: (clientId: string, payload: CreateAddressPayload) =>
    api.post<Address>(`/clients/${clientId}/addresses`, payload),

  /** Ändern, ohne die ID zu verlieren — sonst wäre es eine andere Adresse. */
  updateAddress: (clientId: string, addressId: string, payload: UpdateAddressPayload) =>
    api.patch<Address>(`/clients/${clientId}/addresses/${addressId}`, payload),

  deleteAddress: (clientId: string, addressId: string) =>
    api.delete<{ message: string }>(`/clients/${clientId}/addresses/${addressId}`),

  // Contacts
  addContact: (clientId: string, payload: CreateContactPayload) =>
    api.post<ContactPerson>(`/clients/${clientId}/contacts`, payload),

  updateContact: (clientId: string, contactId: string, payload: Partial<CreateContactPayload>) =>
    api.patch<ContactPerson>(`/clients/${clientId}/contacts/${contactId}`, payload),

  deleteContact: (clientId: string, contactId: string) =>
    api.delete<{ message: string }>(`/clients/${clientId}/contacts/${contactId}`),
};
